import { useState, useEffect } from 'react';
import { CalendarDays, CheckCircle, XCircle, Clock, AlertTriangle, ChevronLeft, ChevronRight, Home, Users, Bell, Settings, X, BookOpen, FileText, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';

interface FilhoResumo {
  vinculoId: string;
  aluno: {
    id: string;
    nome: string;
    fotoUrl?: string;
    escola?: { id: string; nome: string };
    turma?: { id: string; nome: string; serie: string; turno: string };
  };
}

interface PresencaItem {
  id: string;
  alunoId: string;
  dataReferencia: string;
  statusPresencaDiaria: string;
  totalPresencas: number;
  totalAusencias: number;
  totalChamadas: number;
}

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function Frequencia() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const responsavelId = useResponsavelId();
  const { hojeStr: simHojeStr } = useHojeReferencia();

  const [filhos, setFilhos] = useState<FilhoResumo[]>([]);
  const [selectedAluno, setSelectedAluno] = useState('');
  const [presencas, setPresencas] = useState<PresencaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPresencas, setLoadingPresencas] = useState(false);
  const [mesOffset, setMesOffset] = useState(0);

  // Modal State
  const [selectedClassDetail, setSelectedClassDetail] = useState<{ dia: number, weekday: string, horario: number, status: string } | null>(null);

  // Buscar filhos
  useEffect(() => {
    if (!responsavelId) return;
    api.get(ROUTES.mobileResponsavel.resumo(responsavelId))
      .then((res) => {
        const alunos = (res.data?.alunos || []).map((a: any) => ({
          vinculoId: a.vinculoId,
          aluno: a.aluno,
        }));
        setFilhos(alunos);
        if (alunos.length > 0) setSelectedAluno(alunos[0].aluno.id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [responsavelId]);

  // Buscar presenças
  useEffect(() => {
    if (!selectedAluno) return;
    const filho = filhos.find(f => f.aluno.id === selectedAluno);
    const escolaId = filho?.aluno?.escola?.id;
    if (!escolaId) return;

    const [sy, sm, sd] = (simHojeStr || '').split('-').map(Number);
    const now = sy && sm && sd ? new Date(sy, sm - 1, sd) : new Date();
    now.setMonth(now.getMonth() + mesOffset);
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    setLoadingPresencas(true);
    api.get(ROUTES.mobileResponsavel.frequencia(responsavelId, selectedAluno, startDate, endDate))
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [res.data];
        const studentData = list.find((item: any) => item.aluno?.id === selectedAluno) || list[0];
        const items = studentData?.dias || [];
        setPresencas(Array.isArray(items) ? items : []);
      })
      .catch(() => setPresencas([]))
      .finally(() => setLoadingPresencas(false));
  }, [responsavelId, selectedAluno, mesOffset, filhos, simHojeStr]);

  const [sy, sm, sd] = (simHojeStr || '').split('-').map(Number);
  const now = sy && sm && sd ? new Date(sy, sm - 1, sd) : new Date();
  now.setMonth(now.getMonth() + mesOffset);
  const year = now.getFullYear();
  const month = now.getMonth();
  const mesLabel = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const totalP = presencas.filter((p) =>
    ['PRESENTE', 'ATRASADO', 'PARCIAL', 'SAIU', 'JUSTIFICADA'].includes(p.statusPresencaDiaria),
  ).length;
  const totalA = presencas.filter((p) =>
    ['AUSENTE', 'INCONSISTENTE'].includes(p.statusPresencaDiaria),
  ).length;
  const pct = (totalP + totalA) > 0 ? Math.round((totalP / (totalP + totalA)) * 100) : 0;

  const selectedFilho = filhos.find(f => f.aluno.id === selectedAluno);

  // Gerar Grade Diária
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysList = [];
  const weekdayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  
  for (let i = 1; i <= daysInMonth; i++) {
    const dateObj = new Date(year, month, i);
    const dayOfWeek = dateObj.getDay(); 
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    daysList.push({
      day: i,
      weekday: weekdayNames[dayOfWeek],
      isWeekend: dayOfWeek === 0,
      dateStr
    });
  }

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    // Alinhamento dos dias na semana para calendário de 7 colunas
    const firstDayIndex = new Date(year, month, 1).getDay();
    const paddingCells = Array.from({ length: firstDayIndex }, (_, i) => ({
      day: 0,
      weekday: weekdayNames[i],
      isWeekend: i === 0 || i === 6,
      dateStr: '',
      isPadding: true,
    }));

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <CalendarDays size={12} color="#818cf8" /> Frequência & Diário de Classe
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Frequência & Presença Escolar
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Acompanhamento diário de presença, controle de faltas e conformidade LDB
            </p>
          </div>

          {/* Hero KPIs */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{totalP}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Presenças</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f87171', lineHeight: 1.1 }}>{totalA}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Faltas</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: pct >= 75 ? '#38bdf8' : '#fb923c', lineHeight: 1.1 }}>{pct}%</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Taxa LDB</div>
            </div>
          </div>
        </div>

        {/* Toolbar: Student Selector & Month Navigation */}
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          padding: '12px 18px',
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}>
          {/* Student Selector Tabs */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {filhos.map((f) => (
              <button
                key={f.aluno.id}
                onClick={() => setSelectedAluno(f.aluno.id)}
                style={{
                  padding: '8px 14px', borderRadius: 10, border: '1px solid',
                  borderColor: selectedAluno === f.aluno.id ? '#2563eb' : '#cbd5e1',
                  background: selectedAluno === f.aluno.id ? '#eff6ff' : '#ffffff',
                  color: selectedAluno === f.aluno.id ? '#2563eb' : '#475569',
                  fontWeight: selectedAluno === f.aluno.id ? 700 : 500, fontSize: '0.82rem',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}
              >
                <span>{f.aluno.nome}</span>
                {f.aluno.turma?.nome && <span style={{ fontSize: '0.72rem', opacity: 0.7 }}>({f.aluno.turma.nome})</span>}
              </button>
            ))}
          </div>

          {/* Month Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 4, gap: 8 }}>
            <button
              onClick={() => setMesOffset((m) => m - 1)}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <ChevronLeft size={16} color="#475569" />
            </button>
            <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', textTransform: 'capitalize', minWidth: 140, textAlign: 'center' }}>
              {mesLabel}
            </span>
            <button
              onClick={() => setMesOffset((m) => m + 1)}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <ChevronRight size={16} color="#475569" />
            </button>
          </div>
        </div>

        {/* ═══ Main Split: 7-Column Calendar Grid + Side Summary ═══ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 0.75fr)',
          gap: 20,
          alignItems: 'start',
          width: '100%',
        }}>
          {/* Grade de Presença Mensal 7 Colunas */}
          <div style={{ background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                Calendário de Frequência — {mesLabel}
              </h3>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                {selectedFilho?.aluno.nome}
              </span>
            </div>

            {loadingPresencas ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando dados de presença...</div>
            ) : (
              <>
                {/* Cabeçalho dos dias da semana */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, textAlign: 'center', marginBottom: 8 }}>
                  {weekdayNames.map(d => (
                    <div key={d} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', padding: '4px 0' }}>
                      {d}
                    </div>
                  ))}
                </div>

                {/* Grade dos dias */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                  {paddingCells.map((p, idx) => (
                    <div key={`pad-${idx}`} style={{ minHeight: 64, background: '#f8fafc', borderRadius: 10, opacity: 0.3 }} />
                  ))}
                  {daysList.map((d) => {
                    const p = presencas.find((item) => item.dataReferencia?.startsWith(d.dateStr));
                    const isPresent = p && ['PRESENTE', 'ATRASADO', 'PARCIAL', 'SAIU'].includes(p.statusPresencaDiaria);
                    const isAbsent = p && ['AUSENTE', 'INCONSISTENTE'].includes(p.statusPresencaDiaria);

                    return (
                      <div
                        key={d.day}
                        style={{
                          minHeight: 64,
                          background: d.isWeekend ? '#f8fafc' : isPresent ? '#f0fdf4' : isAbsent ? '#fef2f2' : '#ffffff',
                          border: '1px solid',
                          borderColor: d.isWeekend ? '#e2e8f0' : isPresent ? '#bbf7d0' : isAbsent ? '#fecaca' : '#e2e8f0',
                          borderRadius: 12,
                          padding: '6px 4px',
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          boxShadow: isPresent || isAbsent ? '0 1px 2px rgba(0,0,0,0.03)' : 'none',
                        }}
                      >
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: d.isWeekend ? '#94a3b8' : '#0f172a' }}>{d.day}</div>
                        <div style={{
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          color: d.isWeekend ? '#94a3b8' : isPresent ? '#16a34a' : isAbsent ? '#dc2626' : '#cbd5e1',
                          textTransform: 'uppercase',
                          letterSpacing: '-0.02em',
                        }}>
                          {d.isWeekend ? 'FDS' : isPresent ? 'Presente' : isAbsent ? 'Falta' : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Painel Lateral: Resumo Pedagógico e Ações */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Card de Ficha do Aluno */}
            <div style={{ background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <h4 style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={16} color="#2563eb" /> Aluno Selecionado
              </h4>
              <div style={{ fontSize: '0.84rem', color: '#1e293b', fontWeight: 700 }}>
                {selectedFilho?.aluno.nome || 'Nenhum aluno selecionado'}
              </div>
              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 4 }}>
                {selectedFilho?.aluno.turma?.nome ? `Turma: ${selectedFilho.aluno.turma.nome}` : 'Turma Regular'} • {selectedFilho?.aluno.escola?.nome || 'Aluno Conectado'}
              </div>

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <span style={{ color: '#64748b' }}>Assiduidade no Mês:</span>
                  <strong style={{ color: pct >= 75 ? '#16a34a' : '#ea580c' }}>{pct}%</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <span style={{ color: '#64748b' }}>Presenças Registradas:</span>
                  <strong style={{ color: '#10b981' }}>{totalP} dias</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <span style={{ color: '#64748b' }}>Faltas no Período:</span>
                  <strong style={{ color: totalA > 0 ? '#ef4444' : '#64748b' }}>{totalA} dias</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <span style={{ color: '#64748b' }}>Mínimo Exigido LDB:</span>
                  <strong style={{ color: '#2563eb' }}>75%</strong>
                </div>
              </div>
            </div>

            {/* Card de Justificativa de Faltas */}
            <div style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: 20,
              border: '1px solid #bfdbfe',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e40af', fontWeight: 800, fontSize: '0.86rem' }}>
                <FileText size={16} /> Justificar Ausências
              </div>
              <p style={{ fontSize: '0.74rem', color: '#1e3a8a', margin: 0, lineHeight: 1.4 }}>
                Envie atestados médicos ou justificativas para análise da secretaria da escola.
              </p>
              <button
                onClick={() => navigate('/responsavel/justificativas')}
                style={{
                  marginTop: 6,
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '9px 14px',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                Enviar Justificativa / Atestado
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mapear presenças para grade de aulas
  const getHorariosForDay = (p: PresencaItem | undefined, isFuture: boolean, isWeekend: boolean) => {
    if (isWeekend) return [null, null, null, null, null];
    if (isFuture) return ['empty', 'empty', 'empty', 'empty', 'empty'];
    if (!p) return ['empty', 'empty', 'empty', 'empty', 'empty'];
    
    // Usar totalPresencas e totalAusencias reais do backend
    const totalAulas = p.totalChamadas || (p.totalPresencas + p.totalAusencias) || 5;
    const result: string[] = [];
    for (let i = 0; i < 5; i++) {
      if (i < p.totalPresencas) result.push('present');
      else if (i < p.totalPresencas + p.totalAusencias) result.push('absent');
      else result.push('empty');
    }
    
    // Ajuste global por status (INCONSISTENTE = RFID+falta — trata como ausência)
    if (p.statusPresencaDiaria === 'PRESENTE') {
      return result.map(r => r === 'empty' && p.totalChamadas > 0 ? 'present' : r);
    }
    if (
      (p.statusPresencaDiaria === 'AUSENTE' || p.statusPresencaDiaria === 'INCONSISTENTE') &&
      p.totalAusencias > 0
    ) {
      return result.map(r => r === 'empty' && p.totalChamadas > 0 ? 'absent' : r);
    }
    
    return result;
  };

  const renderCellStatus = (status: string) => {
    if (status === 'present') return <CheckCircle size={14} style={{ color: '#10b981' }} strokeWidth={3} />;
    if (status === 'absent') return <XCircle size={14} style={{ color: '#ef4444' }} strokeWidth={3} />;
    return <div style={{ width: 12, height: 12, borderRadius: '50%', border: '1px dashed #cbd5e1' }} />;
  };

  return (
    <div className="resp-mobile-root" style={{ background: '#0b1526' }}>
      {/* ═══ NAVY HEADER COM FILTRO ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 0 24px', display: 'flex', flexDirection: 'column' }}>
        
        {/* TÍTULO E BOTÃO VOLTAR */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', marginBottom: 12, position: 'relative', zIndex: 10 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ background: 'transparent', border: 'none', color: 'white', padding: '8px', marginLeft: '-8px', display: 'flex', cursor: 'pointer' }}
          >
            <ArrowLeft size={22} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>
              Frequência
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>
              Diário de Classe Detalhado
            </p>
          </div>
        </div>

        {/* KPIs Mini */}
        {presencas.length > 0 && (
          <div style={{ display: 'flex', gap: 8, padding: '0 20px', marginBottom: 14, position: 'relative', zIndex: 10 }}>
            {[
              { label: 'Presenças', value: totalP, color: '#4ade80' },
              { label: 'Faltas', value: totalA, color: '#f87171' },
              { label: 'Frequência', value: `${pct}%`, color: pct >= 75 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171' },
            ].map(k => (
              <div key={k.label} style={{
                flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: 10,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: '0.5rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* FILTRO DE FILHOS */}
        {filhos.length > 1 && (
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '0 20px', scrollbarWidth: 'none', msOverflowStyle: 'none', position: 'relative', zIndex: 10 }}>
            {filhos.map((f) => {
              const isSelected = selectedAluno === f.aluno.id;
              const initials = f.aluno.nome.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
              return (
                <div 
                  key={f.aluno.id}
                  onClick={() => setSelectedAluno(f.aluno.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    background: isSelected ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${isSelected ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                    padding: '4px 12px 4px 4px', borderRadius: 20, transition: 'all 0.2s', flexShrink: 0
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: 14, background: isSelected ? '#2563eb' : '#1e293b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.55rem', fontWeight: 700, color: 'white',
                  }}>
                    {initials}
                  </div>
                  <span style={{ color: isSelected ? 'white' : '#94a3b8', fontSize: '0.8rem', fontWeight: isSelected ? 600 : 500 }}>
                    {f.aluno.nome.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 80, marginTop: -16, background: '#f8fafc' }}>
        
        {/* Mês Navigator */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, background: 'white', borderRadius: 16, padding: '10px 12px',
          boxShadow: '0 1px 8px rgba(0,0,0,0.04)'
        }}>
          <button onClick={() => setMesOffset((o) => o - 1)} style={{
            background: '#f1f5f9', border: 'none', borderRadius: 10, width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', cursor: 'pointer'
          }}>
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a', textTransform: 'capitalize' }}>
            {mesLabel}
          </span>
          <button onClick={() => setMesOffset((o) => Math.min(o + 1, 0))} disabled={mesOffset >= 0} style={{
            background: '#f1f5f9', border: 'none', borderRadius: 10, width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', cursor: 'pointer',
            opacity: mesOffset >= 0 ? 0.3 : 1,
          }}>
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Grade de Diário de Classe */}
        {loading || loadingPresencas ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>Carregando diário...</div>
        ) : (
          <div style={{ background: 'white', borderRadius: 16, padding: '16px 12px', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '8px 4px', width: 45, color: '#64748b', fontWeight: 600, textAlign: 'center' }}>Dia</th>
                  <th style={{ padding: '8px 2px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>1º H</th>
                  <th style={{ padding: '8px 2px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>2º H</th>
                  <th style={{ padding: '8px 2px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>3º H</th>
                  <th style={{ padding: '8px 2px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>4º H</th>
                  <th style={{ padding: '8px 2px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>5º H</th>
                </tr>
              </thead>
              <tbody>
                {daysList.map(d => {
                  const p = presencas.find(x => {
                    const refDate = typeof x.dataReferencia === 'string' ? x.dataReferencia.split('T')[0] : '';
                    return refDate === d.dateStr;
                  });
                  
                  const targetDate = new Date(d.dateStr + 'T12:00:00'); 
                  const todayStr = simHojeStr;
                  const todayDate = new Date(todayStr + 'T12:00:00');
                  const isFuture = targetDate > todayDate;

                  const horarios = getHorariosForDay(p, isFuture, d.isWeekend);
                  const isToday = d.dateStr === todayStr;
                  const rowBg = d.isWeekend ? '#f8fafc' : isToday ? '#eff6ff' : 'white';
                  
                  return (
                    <tr key={d.day} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                      <td style={{ padding: '8px 2px', textAlign: 'center', borderRight: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: d.isWeekend ? '#94a3b8' : isToday ? '#2563eb' : '#0f172a' }}>{d.day}</div>
                        <div style={{ fontSize: '0.6rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>{d.weekday}</div>
                      </td>
                      {d.isWeekend ? (
                        <td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500 }}>
                          Final de Semana
                        </td>
                      ) : (
                        horarios.map((h, i) => (
                          <td key={i} style={{ padding: '4px 2px', textAlign: 'center' }}>
                            <button 
                              onClick={() => setSelectedClassDetail({ dia: d.day, weekday: d.weekday, horario: i + 1, status: h as string })}
                              style={{
                                width: '100%', height: 32, borderRadius: 6, border: 'none',
                                background: h === 'present' ? '#ecfdf5' : h === 'absent' ? '#fef2f2' : isToday ? 'white' : '#f8fafc',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', padding: 0
                              }}
                            >
                              {renderCellStatus(h as string)}
                            </button>
                          </td>
                        ))
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* ═══ MODAL DETALHE DA AULA ═══ */}
      {selectedClassDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
          zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
        }} onClick={() => setSelectedClassDetail(null)}>
          <div style={{
            background: 'white', width: '100%', borderTopLeftRadius: 24, borderTopRightRadius: 24,
            padding: '24px 20px 40px', animation: 'slideUp 0.3s ease-out'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: '#0f172a' }}>
                  Detalhes da Aula
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                  Dia {selectedClassDetail.dia} ({selectedClassDetail.weekday}) • {selectedClassDetail.horario}º Horário
                </p>
              </div>
              <button onClick={() => setSelectedClassDetail(null)} style={{
                background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer'
              }}>
                <X size={18} />
              </button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '14px', borderRadius: 12,
              background: selectedClassDetail.status === 'present' ? '#ecfdf5' : selectedClassDetail.status === 'absent' ? '#fef2f2' : '#f8fafc',
              color: selectedClassDetail.status === 'present' ? '#047857' : selectedClassDetail.status === 'absent' ? '#b91c1c' : '#64748b',
            }}>
              {selectedClassDetail.status === 'present' && <><CheckCircle size={18} /> <span style={{ fontWeight: 600 }}>Aluno Presente nesta aula</span></>}
              {selectedClassDetail.status === 'absent' && <><XCircle size={18} /> <span style={{ fontWeight: 600 }}>Aluno Ausente nesta aula</span></>}
              {selectedClassDetail.status === 'empty' && <><Clock size={18} /> <span style={{ fontWeight: 600 }}>Aula não realizada / Sem registro</span></>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB BAR ═══ */}
      <div className="resp-tab-bar">
        {[
          { key: 'home', icon: Home, label: 'Inicio', to: '/responsavel/dashboard' },
          { key: 'filhos', icon: Users, label: 'Filhos', to: '/responsavel/filhos' },
          { key: 'freq', icon: CalendarDays, label: 'Frequencia', to: '/responsavel/frequencia' },
          { key: 'avisos', icon: Bell, label: 'Avisos', to: '/responsavel/comunicados' },
          { key: 'perfil', icon: Settings, label: 'Perfil', to: '/responsavel/perfil' },
        ].map((tab) => (
          <button key={tab.key}
            className={`resp-tab-item ${tab.key === 'freq' ? 'active' : ''}`}
            onClick={() => navigate(tab.to)}>
            <div style={{ position: 'relative' }}>
              <tab.icon size={20} />
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
